import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Ticket, Copy, Loader2, Crown, CheckCircle2, Clock, Lock, MessageCircle } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";

const StatusBadge = ({ status }) => {
  const map = {
    unused: { label: "Sin usar", cls: "border-green-500/40 text-green-400", icon: CheckCircle2 },
    redeemed: { label: "En uso", cls: "border-yellow-400/40 text-yellow-impact", icon: Clock },
    finished: { label: "Usado", cls: "border-zinc-600 text-zinc-500", icon: Lock },
  };
  const s = map[status] || map.finished;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border ${s.cls}`}>
      <Icon size={12} /> {s.label}
    </span>
  );
};

export default function PinsForSale() {
  const [state, setState] = useState(null); // null=loading, {pins, allowance} | {forbidden:true}
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/pins/mine");
      setState(data);
    } catch (err) {
      if (err.response?.status === 403) setState({ forbidden: true });
      else { setState({ pins: [], allowance: 10 }); toast.error(apiError(err.response?.data?.detail)); }
    }
  };

  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/pins/generate");
      setState(data);
      toast.success("PINs actualizados");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const copy = (code) => { navigator.clipboard.writeText(code); toast.success("PIN copiado"); };

  const shareWhatsApp = (code) => {
    const url = `${window.location.origin}/login`;
    const msg =
      `🚀 *¡Te REGALO un acceso PREMIUM a MULTICONTACTOS!*\n\n` +
      `Con este PIN creas una *campaña de marketing COMPLETA con Inteligencia Artificial* en minutos: mensajes que venden, imágenes con tu marca, video corto y un embudo que CAPTA prospectos calificados directo a tu WhatsApp. 🤖🔥\n\n` +
      `Sin pagar. Sin registrarte. Sin saber de tecnología.\n\n` +
      `🎟️ *Tu PIN:* ${code}\n\n` +
      `👉 Actívalo aquí: ${url}\n` +
      `(toca *"Tengo un PIN de campaña"* y escríbelo)\n\n` +
      `⚠️ Es de *un solo uso*. ¡Aprovéchalo HOY y empieza a generar prospectos! 💪`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (state === null) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-purple-500" size={32} /></div>;
  }

  if (state.forbidden) {
    return (
      <div data-testid="pins-locked" className="max-w-xl">
        <div className="flex items-center gap-3 mb-2">
          <Ticket className="text-yellow-400" size={26} />
          <h1 className="font-display font-black tracking-tight text-3xl">PINs para Vender</h1>
        </div>
        <div className="mt-6 rounded-2xl border border-yellow-400/30 bg-[#16130a] p-8 text-center">
          <Crown className="mx-auto text-yellow-400 mb-3" size={32} />
          <h2 className="font-display font-bold text-xl mb-2">Exclusivo del plan Élite</h2>
          <p className="text-zinc-400 mb-5">Con el plan Élite recibes <strong className="text-yellow-impact">10 PINs cada mes</strong> que puedes regalar o vender al precio que tú decidas. Quien los use podrá crear una campaña completa con IA sin registrarse.</p>
          <Link to="/dashboard/planes"><Button className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl px-6 py-5">Ver plan Élite</Button></Link>
        </div>
      </div>
    );
  }

  const pins = state.pins || [];
  const unused = pins.filter((p) => p.status === "unused");
  const inUse = pins.filter((p) => p.status === "redeemed");
  const used = pins.filter((p) => p.status === "finished");

  return (
    <div data-testid="pins-page">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-2">
        <div className="flex items-center gap-3">
          <Ticket className="text-yellow-400" size={26} />
          <h1 className="font-display font-black tracking-tight text-3xl">PINs para Vender</h1>
        </div>
        <Button data-testid="generate-pins" onClick={generate} disabled={generating} className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-full px-6 py-5">
          {generating ? <Loader2 className="animate-spin mr-2" size={16} /> : <Ticket size={16} className="mr-2" />} Generar PINs para campaña
        </Button>
      </div>
      <p className="text-zinc-400 mb-2">Tienes <strong className="text-yellow-impact">{unused.length}</strong> PIN(s) sin usar de {state.allowance} mensuales. Véndelos o regálalos al precio que tú decidas.</p>
      <p className="text-xs text-zinc-500 mb-6">Cada PIN permite a su tenedor crear UNA campaña completa con IA (mensaje, imágenes, video, embudo y captación de prospectos a su WhatsApp), sin registrarse ni pagar. Se renuevan cada mes: los no usados se conservan y los usados se reemplazan con nuevos (máximo {state.allowance}).</p>

      {/* Contadores */}
      <div className="grid grid-cols-3 gap-3 mb-8 max-w-xl">
        <div data-testid="count-unused" className="rounded-xl border border-green-500/30 bg-green-500/5 p-4 text-center">
          <div className="font-display font-black text-2xl text-green-400">{unused.length}</div>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mt-1">Disponibles</div>
        </div>
        <div data-testid="count-inuse" className="rounded-xl border border-yellow-400/30 bg-yellow-400/5 p-4 text-center">
          <div className="font-display font-black text-2xl text-yellow-impact">{inUse.length}</div>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mt-1">En uso</div>
        </div>
        <div data-testid="count-used" className="rounded-xl border border-white/10 bg-[#121212] p-4 text-center">
          <div className="font-display font-black text-2xl text-zinc-300">{used.length}</div>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mt-1">Vendidos / usados</div>
        </div>
      </div>

      {pins.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-[#121212] p-10 text-center">
          <Ticket className="mx-auto text-zinc-600 mb-3" size={32} />
          <p className="text-zinc-400">Aún no tienes PINs. Haz clic en "Generar PINs para campaña".</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {pins.map((p) => (
            <div key={p.pin_id} data-testid="pin-card" className={`rounded-2xl border p-5 ${p.status === "unused" ? "border-purple-500/30 bg-[#0e0a16]" : "border-white/10 bg-[#121212]"}`}>
              <div className="flex items-center justify-between mb-3">
                <StatusBadge status={p.status} />
                {p.status === "unused" && (
                  <button data-testid="copy-pin" onClick={() => copy(p.code)} className="text-zinc-400 hover:text-white" title="Copiar PIN"><Copy size={16} /></button>
                )}
              </div>
              <div className={`font-mono font-black text-xl tracking-wider ${p.status === "finished" ? "text-zinc-600 line-through" : "text-white"}`}>{p.code}</div>
              {p.status === "unused" && (
                <Button data-testid="share-pin" onClick={() => shareWhatsApp(p.code)} className="mt-4 w-full bg-green-600 hover:bg-green-500 text-white font-semibold rounded-xl">
                  <MessageCircle size={16} className="mr-2" /> Compartir por WhatsApp
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 rounded-2xl border border-white/10 bg-[#121212] p-6">
        <h3 className="font-display font-bold text-lg mb-2">¿Cómo lo usa tu comprador?</h3>
        <ol className="text-sm text-zinc-400 space-y-1.5 list-decimal list-inside">
          <li>Entra a la página de inicio de sesión y elige "Tengo un PIN de campaña".</li>
          <li>Escribe el PIN y su WhatsApp donde recibirá prospectos.</li>
          <li>Crea su campaña con IA (texto, imágenes, video y embudo) y la publica.</li>
          <li>La IA califica a los prospectos y envía los mejores a su WhatsApp.</li>
        </ol>
      </div>
    </div>
  );
}
